import React from 'react';
import {classes, style} from './doppe-page-background-view.st.css';
import {
    BymoPageBackgroundImageLayout,
    BymoPageBackgroundMediaSettingsDefaultValues,
    useBymoPageProps
} from "../../client-server-common";
import {
    cssAddUnitsToNumericValue,
    evaluateFunction,
    HtmlObjectHorizontalPosition,
    HtmlObjectResizeMode,
    HtmlObjectVerticalPosition
} from "@wix/devzai-utils-common";
import {WixImageView} from "@wix/devzai-common-client";
import {ImageViewPositionHorizontal, ImageViewPositionVertical, ImageViewResizeMode} from "@wix/devzai-utils-react";
import {
    wixImageInfoGetScaleToFitImageUrl,
    wixImageResourceGetRawImageUrl,
    wixImageResourceIsMediaManagerImageInfo,
    wixMediaResourceIsWixImageResource,
    WixVideoInfoQualitySelector
} from "@wix/devzai-common-wix";
import {DoppePageVideoMediaView} from "../doppe-page-video-media-view/doppe-page-video-media-view";

export const DoppePageBackgroundView = React.memo(function DoppePageBackgroundView (props: DoppePageBackgroundView.Props) {
    const {
        className,
        ...htmlAttributes
    } = props;

    const bymoPageProps = useBymoPageProps();

    const backgroundImage = bymoPageProps.backgroundImage;

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className)}
        >
            {evaluateFunction(() => {
                if (backgroundImage) {

                    const mediaInfo = backgroundImage.mediaInfo;

                    if (wixMediaResourceIsWixImageResource(mediaInfo)) {

                        const layout = backgroundImage.layout ?? BymoPageBackgroundImageLayout.Fill;

                        switch (layout) {
                            case BymoPageBackgroundImageLayout.Fill: {
                                return (
                                    <WixImageView
                                        className={style(classes.backgroundImage, {})}
                                        src={mediaInfo}
                                        resizeMode={ImageViewResizeMode.Cover}
                                        positionVertical={backgroundImage.verticalPosition ?? ImageViewPositionVertical.Center}
                                        positionHorizontal={backgroundImage.horizontalPosition ?? ImageViewPositionHorizontal.Center}
                                        placeholderSrc={doppePageBackgroundViewGetInitialSource}
                                        style={{
                                            opacity: backgroundImage.opacity !== undefined ? backgroundImage.opacity / 100 : 1
                                        }}
                                    />
                                )
                            }
                            case BymoPageBackgroundImageLayout.Tile: {
                                return (
                                    <div
                                        className={style(classes.backgroundImage, {})}
                                        style={{
                                            backgroundImage: `url(${wixImageResourceGetRawImageUrl(mediaInfo)})`,
                                            backgroundRepeat: 'repeat',
                                            opacity: backgroundImage.opacity ? backgroundImage.opacity / 100 : 1,
                                            backgroundSize: cssAddUnitsToNumericValue(
                                                backgroundImage.tileLayoutSize ?? BymoPageBackgroundMediaSettingsDefaultValues.tileLayoutSize,
                                                '%'),
                                            backgroundPositionX: backgroundImage.tileHorizontalPosition ?? BymoPageBackgroundMediaSettingsDefaultValues.tileHorizontalPosition
                                        }}
                                    />
                                )
                            }
                        }

                    } else {
                        return (
                            <>
                                {mediaInfo.poster ? (
                                    <WixImageView
                                        className={style(classes.videoMediaPoster, {})}
                                        src={mediaInfo.poster}
                                        resizeMode={ImageViewResizeMode.Cover}
                                        positionVertical={HtmlObjectVerticalPosition.Center}
                                        positionHorizontal={HtmlObjectHorizontalPosition.Center}
                                        placeholderSrc={doppePageBackgroundViewGetInitialSource}
                                        style={{
                                            opacity: backgroundImage.opacity !== undefined ? backgroundImage.opacity / 100 : 1
                                        }}
                                    />
                                ) : null}
                                <DoppePageVideoMediaView
                                    className={style(classes.backgroundImage, {})}
                                    resizeMode={HtmlObjectResizeMode.Cover}
                                    quality={WixVideoInfoQualitySelector.Highest}
                                    positionVertical={HtmlObjectVerticalPosition.Center}
                                    positionHorizontal={HtmlObjectHorizontalPosition.Center}
                                    src={mediaInfo}
                                    autoPlay={true}
                                    loop={true}
                                    muted={true}
                                    playsInline={true}
                                    style={{
                                        opacity: backgroundImage.opacity ? backgroundImage.opacity / 100 : 1
                                    }}
                                />
                            </>
                        );
                    }


                } else {
                    return null;
                }
            })}
        </div>
    );
});

export namespace DoppePageBackgroundView {

    export interface Props extends React.HTMLAttributes<any> {

    }
}

export function doppePageBackgroundViewGetInitialSource (imageSrc: WixImageView.Source) {
    if (imageSrc && wixImageResourceIsMediaManagerImageInfo(imageSrc)) {
        return wixImageInfoGetScaleToFitImageUrl(imageSrc, {
            targetWidth: 100,
            targetHeight: 100,
            quality: 80,
            filters: {
                blur: 3
            }
        });
    } else {
        return undefined;
    }
}